import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField
} from '@mui/material';
import { SRAccount } from '@api/v1';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../core/actions';
import { i18n } from '../../../i18n';
import { api } from '../../api';

interface WebhookFormState {
	url: string;
	clientId: string;
	clientSecret: string;
	accessTokenUrl: string;
}

const initialFormState: WebhookFormState = {
	url: '',
	clientId: '',
	clientSecret: '',
	accessTokenUrl: ''
};

export function AddWebhookButton({ account }: { account: SRAccount }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [formState, setFormState] = useState(initialFormState);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		setOpen(false);
		setFormState(initialFormState);
	};

	const handleChange = (field: keyof WebhookFormState) => (event) => {
		setFormState((currentState) => ({
			...currentState,
			[field]: event.target.value
		}));
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);

		try {
			await api.post(`/api/sysadmin/accounts/${account.id}/webhook`, formState);
			dispatch(showNotification(i18n.t('sysadmin:webhook_save_success'), 'success'));
			handleClose();
		} catch {
			dispatch(showNotification(i18n.t('sysadmin:webhook_save_error'), 'error'));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Button variant="outlined" color="primary" onClick={() => setOpen(true)}>
				{t('sysadmin:add_webhook')}
			</Button>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
				<DialogTitle>{t('sysadmin:add_webhook')}</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						{t('sysadmin:webhook_credentials_hint')}
					</DialogContentText>
					<TextField
						autoFocus
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_url')}
						value={formState.url}
						onChange={handleChange('url')}
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_client_id')}
						value={formState.clientId}
						onChange={handleChange('clientId')}
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_client_secret')}
						value={formState.clientSecret}
						onChange={handleChange('clientSecret')}
						type="password"
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_access_token_url')}
						value={formState.accessTokenUrl}
						onChange={handleChange('accessTokenUrl')}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary" disabled={isSubmitting}>
						{t('sysadmin:restrictions_dialog_cancel')}
					</Button>
					<Button
						onClick={handleSubmit}
						color="primary"
						disabled={
							!formState.url.trim() ||
							!formState.clientId.trim() ||
							!formState.clientSecret.trim() ||
							!formState.accessTokenUrl.trim() ||
							isSubmitting
						}
					>
						{t('sysadmin:restrictions_dialog_save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
